import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonBase, Dialog } from "@mui/material";
import { useEditor, useNode } from "@craftjs/core";
import { Spacing } from "../settings/SpacingSettings/SpacingSettings";
import { DimentionProps } from "@/components/editor/settings/DimentionSettings/types";
import useNormalizeDimentions from "@/components/editor/settings/DimentionSettings/useNormalizeDimentions";
import useNormalizeSpacing from "@/components/editor/settings/SpacingSettings/useNormalizeSpacing";
import useNormalizeBorderRadius from "@/components/editor/settings/BorderRadius/useNormalizeBorderRadius";
import { BorderRadius } from "@/components/editor/settings/BorderRadius/BorderRadiusSettings";
import ImageSettings, { ImageProps } from "@/components/editor/settings/ImageSettings/ImageSettings";
import useNormalizeImageSettings from "@/components/editor/settings/ImageSettings/useNormalizeImageSettings";
import useResponsiveValues from "../hooks/useResponsiveValues";
import { CssClass } from "../settings/ClassSettings";
import useVariableValues from "../hooks/useVariableValues";
import { RepeatContext } from "./RepeatBlock";
import { IMAGE_PLACEHOLDER } from "@/constants/global";

export type Props = Partial<ImageProps & DimentionProps & BorderRadius & Spacing & CssClass>;

const ImageBlock = (props: Props) => {
  const responsive = useResponsiveValues();
  const dimentions = useNormalizeDimentions(props);
  const spacing = useNormalizeSpacing(props);
  const borderRadius = useNormalizeBorderRadius(props);
  const { src, modal, href, target, ...imageProps } = useNormalizeImageSettings(props);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const vv = useVariableValues();
  const navigate = useNavigate();
  const repeatContext = useContext(RepeatContext);

  const { query } = useEditor();
  const {
    id,
    connectors: { connect, drag },
  } = useNode((state) => {
    return {
      hasSelectedNode: state.events.selected,
      hasDraggedNode: state.events.dragged,
    };
  });

  const isEditorEnabled = query.getOptions().enabled;

  const handleClick = () => {
    if (isEditorEnabled) return;
    if (repeatContext.href) {
      window.open(repeatContext.href);
      return;
    }
    if (props?.href && typeof props?.href === "string") {
      const url = vv.getValue(props.href ?? "");
      if (responsive.getValue(props.target) === "_blank") {
        window.open(url, "_blank");
      } else {
        if (url.startsWith("?")) {
          const qs = new URLSearchParams(url);
          navigate(document.location.pathname + "?" + qs.toString());
        } else {
          navigate(url);
        }
      }
    }
  };

  const Image = () => (
    <img
      // className={`img ${responsive.getValue(cssClass)}`}
      style={{
        ...spacing,
        ...dimentions,
        ...borderRadius,
        ...imageProps,
      }}
      src={responsive.getValue(src as string) ?? IMAGE_PLACEHOLDER}
    />
  );

  return (
    <div
      className={`img ${responsive.getValue(props.cssClass)}`}
      ref={(ref) => connect(drag(ref))}
      key={`${id}-${responsive.viewport}`}
      style={{
        ...dimentions,
      }}
    >
      {modal ? (
        <>
          <ButtonBase
            disableRipple
            disabled={isEditorEnabled}
            onClick={() => {
              setIsModalOpen(true);
            }}
          >
            <Image />
          </ButtonBase>
          <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <img width="auto" height="500" src={responsive.getValue(src as string) ?? IMAGE_PLACEHOLDER} />
          </Dialog>
        </>
      ) : href ? (
        <ButtonBase component="a" type="button" target={target} onClick={handleClick}>
          <Image />
        </ButtonBase>
      ) : (
        <Image />
      )}
    </div>
  );
};

ImageBlock.craft = {
  displayName: "Imagen",
  props: {
    dimentions: {
      width: "100",
      height: "unset",
      widthType: "%",
      heightType: "px",
    },
  },
  related: {
    settings: ImageSettings,
  },
};

export default ImageBlock;
